import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { providerApi } from "@/lib/providerApi";
import { usePageMeta } from "@/lib/site";
import { ListingForm, emptyListing, formToBody, validateListing, useModules } from "@/components/partner/ListingForm";
import { PlanPicker } from "@/components/partner/PlanPicker";

const STEPS = ["Email", "Declaration", "Your listing", "Plan"];

/**
 * Become a partner on the web: confirm email → accept the provider declaration → first listing (saved as a
 * server-side draft) → pick a plan. A £0 plan publishes the draft immediately; a paid plan publishes it the
 * moment Stripe confirms (the backend does that, so closing the tab mid-checkout never loses it).
 */
export default function PartnerStart() {
  usePageMeta("Become a partner", "List your business on Fable + and take bookings across every world.");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mods = useModules();
  const [decl, setDecl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [form, setForm] = useState(emptyListing(params.get("world") || ""));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  const emailOk = user?.email_verified !== false;

  useEffect(() => {
    providerApi.declaration().then(setDecl).catch(() => setDecl(null));
    providerApi.draft().then((d) => {
      if (d?.draft?.payload) {
        setDraftSaved(true);
        const p = d.draft.payload;
        setForm({ ...emptyListing(p.module), ...p, price: p.price != null ? String(p.price) : "", images: p.images || [], services: (p.services || []).map((s) => ({ ...s, price: String(s.price ?? ""), duration: s.duration != null ? String(s.duration) : "" })) });
      }
    }).catch(() => {});
  }, []);

  // Land on the right step: skip what's done.
  useEffect(() => {
    if (decl === null && !user) return;
    const want = params.get("step");
    if (want === "plan" && draftSaved) return setStep(3);
    if (!emailOk) return setStep(0);
    if (decl && !decl.accepted) return setStep(1);
    setStep(draftSaved ? 3 : 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decl, emailOk, draftSaved]);

  if (user && user.is_partner && user.provider_status === "active") {
    return (
      <Shell step={3}>
        <h2 className="text-xl font-bold">You're already a partner</h2>
        <p className="mt-2 text-sm text-muted-foreground">Add more listings from your partner hub — one plan covers every world.</p>
        <Link to="/partner/listings/new"><Button className="mt-5 rounded-full">Add a listing</Button></Link>
      </Shell>
    );
  }

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await providerApi.verifyEmail(code.trim());
      await refresh();
      toast.success("Email confirmed");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      await providerApi.acceptDeclaration(decl.version);
      setDecl({ ...decl, accepted: true });
      await refresh();
      setStep(2);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    const problem = validateListing(form);
    if (problem) return toast.error(problem);
    setBusy(true);
    try {
      await providerApi.saveDraft(formToBody(form));
      setDraftSaved(true);
      setStep(3);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPlan = async (res) => {
    if (res?.redirecting) return;
    await refresh();
    toast.success(res?.listing ? "Your listing is live" : "You're set up");
    navigate("/partner", { replace: true });
  };

  return (
    <Shell step={step}>
      {step === 0 && (
        <div>
          <div className="flex items-center gap-3"><MailCheck className="text-primary" /><h2 className="text-xl font-bold">Confirm your email</h2></div>
          <p className="mt-2 text-sm text-muted-foreground">Selling needs a confirmed email. We've sent a 6-digit code to <b>{user?.email}</b>.</p>
          <form onSubmit={verify} className="mt-4 flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" className="max-w-[180px] text-center text-lg tracking-widest" data-testid="verify-code" />
            <Button type="submit" className="rounded-full" disabled={busy || code.length < 6}>Confirm</Button>
            <Button type="button" variant="ghost" onClick={() => providerApi.resendVerification().then(() => toast.success("Sent a new code")).catch((e) => toast.error(e.message))}>Send a new code</Button>
          </form>
        </div>
      )}

      {step === 1 && decl && (
        <div>
          <h2 className="text-xl font-bold">{decl.title}</h2>
          <ul className="mt-4 space-y-3 text-sm">
            {decl.points.map((p, i) => <li key={i} className="flex gap-3"><Check size={16} className="mt-0.5 shrink-0 text-primary" /><span>{p}</span></li>)}
          </ul>
          {decl.footer && <p className="mt-4 text-xs text-muted-foreground">{decl.footer}</p>}
          <Button className="mt-6 rounded-full" onClick={accept} disabled={busy} data-testid="decl-accept">I confirm all of this</Button>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2 className="text-xl font-bold">Your first listing</h2>
          <p className="mt-1 text-sm text-muted-foreground">You can edit everything later and add more listings in any world.</p>
          <div className="mt-6"><ListingForm value={form} onChange={setForm} mods={mods} /></div>
          <Button className="mt-6 rounded-full" onClick={saveDraft} disabled={busy} data-testid="start-continue">{busy ? "Saving…" : "Continue to choose a plan"}</Button>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2 className="text-xl font-bold">Choose your plan</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {draftSaved ? <>“{form.title}” is saved. </> : null}Pick a plan and it goes live — a £0 plan straight away, a paid plan as soon as checkout completes.
            {" "}<button type="button" className="underline" onClick={() => setStep(2)}>Edit the listing</button>
          </p>
          <div className="mt-5"><PlanPicker onApplied={onPlan} /></div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ step, children }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--provider)" }}>Become a partner</p>
      <h1 className="mt-1 font-serif-display text-3xl font-bold">Sell on Fable +</h1>
      <ol className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
        {STEPS.map((s, i) => (
          <li key={s} className={`rounded-full px-3 py-1 ${i === step ? "bg-primary text-white" : i < step ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{i + 1}. {s}</li>
        ))}
      </ol>
      <div className="mt-6 rounded-3xl border border-border bg-card p-6 sm:p-8">{children}</div>
    </div>
  );
}
